import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../../lib/supabase';
import { useVertical } from '../../../../context/VerticalContext';
import { reportError } from '../../../../lib/errors';

/** The four lanes. Keys match fn_get_followups. */
export type FollowUpLane = 'mot' | 'visit' | 'advisory' | 'quote';

export interface FollowUp {
    lane: FollowUpLane;
    source_id: string;
    item_key: string;
    customer_id: string | null;
    customer_name: string;
    customer_email: string | null;
    customer_phone: string | null;
    vehicle_id: string | null;
    vrm: string | null;
    vehicle_desc: string | null;
    headline: string;
    detail: string;
    due_on: string;
    days_overdue: number;
    severity: string | null;
}

export type FollowUpCounts = Partial<Record<FollowUpLane, number>>;

export const LANE_META: Record<FollowUpLane, { label: string; blurb: string; empty: string }> = {
    mot: {
        label: 'MOT due',
        blurb: 'Expiring in the next month',
        empty: 'No MOTs due in the next month. Add expiry dates by looking a reg up.',
    },
    visit: {
        label: 'Due a visit',
        blurb: 'No work for over a year',
        empty: 'Everyone has been in within the last year.',
    },
    advisory: {
        label: 'Follow-ups from a check',
        blurb: 'Advisories you flagged that are now due',
        empty: 'Nothing due yet. Advisories from your health checks show up here when their reminder date arrives.',
    },
    quote: {
        label: 'Quotes waiting',
        blurb: 'Sent over a week ago, no answer',
        empty: 'No quotes are sitting unanswered.',
    },
};

/** Lane counts for the Clients page. */
export const useFollowUpCounts = () => {
    const { activeOrgId } = useVertical();
    const [counts, setCounts] = useState<FollowUpCounts>({});
    const [loading, setLoading] = useState(true);

    // `refresh` bumps a token rather than re-running a callback that setStates,
    // so the fetch lives in the effect and can be cancelled — otherwise a slow
    // response for the previous org lands after the new one and overwrites it.
    const [reloadToken, setReloadToken] = useState(0);
    const refresh = useCallback(() => setReloadToken((t) => t + 1), []);

    useEffect(() => {
        if (!activeOrgId) return;
        let cancelled = false;
        (async () => {
            const { data, error } = await supabase.rpc('fn_get_followup_counts', { p_org_id: activeOrgId });
            if (cancelled) return;
            if (error) reportError(error, 'use-followups.fn_get_followup_counts');
            setCounts((data as FollowUpCounts) ?? {});
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [activeOrgId, reloadToken]);

    return { counts, loading, refresh };
};

/** The rows for one lane. */
export const useFollowUps = (lane: FollowUpLane | null) => {
    const { activeOrgId } = useVertical();
    const [items, setItems] = useState<FollowUp[]>([]);
    const [loading, setLoading] = useState(true);

    // Same token pattern as the counts above — switching lanes mid-fetch must
    // not let the old lane's rows land in the new one.
    const [reloadToken, setReloadToken] = useState(0);
    const refresh = useCallback(() => setReloadToken((t) => t + 1), []);

    useEffect(() => {
        if (!activeOrgId || !lane) return;
        let cancelled = false;
        (async () => {
            const { data, error } = await supabase.rpc('fn_get_followups', {
                p_org_id: activeOrgId,
                p_lane: lane,
                p_limit: 500,
            });
            if (cancelled) return;
            if (error) reportError(error, 'use-followups.fn_get_followups');
            setItems((data as FollowUp[]) ?? []);
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [activeOrgId, lane, reloadToken]);

    /**
     * Clear one follow-up. Optimistic: the row leaves immediately, because the
     * alternative on a slow workshop connection is tapping Done and watching
     * nothing happen. A failure puts it back and says so.
     */
    const dismiss = useCallback(
        async (item: FollowUp, snoozeMonths?: number): Promise<boolean> => {
            if (!activeOrgId) return false;
            const before = items;
            setItems((prev) =>
                prev.filter((i) => !(i.source_id === item.source_id && i.item_key === item.item_key)),
            );
            const { error } = await supabase.rpc('fn_dismiss_followup', {
                p_org_id: activeOrgId,
                p_lane: item.lane,
                p_source_id: item.source_id,
                p_item_key: item.item_key,
                p_snooze_months: snoozeMonths ?? null,
            });
            if (error) {
                reportError(error, 'use-followups.fn_dismiss_followup');
                setItems(before);
                return false;
            }
            return true;
        },
        [activeOrgId, items],
    );

    return { items, loading, refresh, dismiss };
};
